<template>
  <div
    class="cart-drawer critical-component-hide"
    :class="{'cart-drawer--has-product-upsell' : hasLocalProducts }"
  >
    <product-upsell
      v-if="!isMobile && cart.item_count > 0"
      :liquid="liquid"
      :products="products"
      :product-count="productCount"
      :recommendations="recommendations"
      product-upsell-splide-settings-cart-drawer
      @has-local-products="handleHasLocalProducts"
    />

    <div class="cart-drawer__container">
      <p class="cart-drawer__header">
        <button
          class="cart-drawer__close icon-button"
          :aria-label="$string('accessibility.close')"
          type="button"
          @click="closeOverlay('cartDrawer')"
        >
          <icon-close />
        </button>

        <span
          class="cart-drawer__title text-heading-xl text-heading-2xl-desktop"
          v-text="$string('cart.title')"
        />

        <span
          class="
          cart-drawer__item-count
          text-body-xs-regular
          text-body-s-regular-desktop
        "
          v-text="$string(
            'cart.item_count',
            {
              pluralise: cart.item_count,
              replace: { count: cart.item_count },
            },
          )"
        />
      </p>

      <free-shipping-indicator
        v-if="cart.item_count > 0"
        class="cart-drawer__free-shipping-indicator"
        :free-shipping-threshold="freeShippingThreshold"
        display-collectors-club-membership
      />

      <form
        v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
        class="cart-drawer__form"
        :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
        method="post"
        @submit="handleCheckoutSubmit"
      >
        <div
          class="cart-drawer__body"
          js-overlay="scrollElement"
        >
          <div class="cart-drawer__line-items">
            <template v-if="!isCollectorsClubMember && freeMembershipThresholdReached">
              <line-item-card
                class="cart-drawer__line-item-card"
                v-bind="freeMembershipContent"
                template="cart-drawer"
              />
              <divider-line />
            </template>

            <template
              v-for="(lineItem, index) of cartItems"
              :key="getCartItemKey(lineItem)"
            >
              <line-item
                class="cart-drawer__line-item"
                :line-item="lineItem"
                template="cart-drawer"
              />

              <divider-line v-if="(index + 1) !== cartItems.length" />
            </template>

            <divider-line v-if="isMobile" />

            <product-upsell
              v-if="isMobile"
              :liquid="liquid"
              :products="products"
              :product-count="productCount"
              :recommendations="recommendations"
              product-upsell-splide-settings-cart-drawer
              @has-local-products="handleHasLocalProducts"
            />
          </div>

          <cart-summary
            class="cart-drawer__summary"
            template="cart-drawer"
          />
        </div>

        <div class="cart-drawer__footer">
          <btn
            class="cart-summary__submit"
            :label="$string('cart.checkout')"
            name="checkout"
            type="submit"
          />

          <btn
            class="cart-summary__basket"
            class-label="text-body-s-underline"
            :label="$string('cart.view_cart')"
            modifiers="text center"
            :url="$variable('routes.cart.url')"
          />
        </div>
      </form>

      <div
        v-else
        class="cart-drawer__empty-container"
        js-overlay="scrollElement"
      >
        <div class="cart-drawer__empty-content">
          <p
            class="
              cart-drawer__empty-message
              text-body-s-regular
            "
            v-text="$string('cart.empty.drawer_text')"
          />

          <btn
            v-if="emptyCartCta.ctaUrl"
            class="cart-drawer__empty-cta"
            :label="emptyCartCta.ctaText"
            modifiers="primary"
            :url="emptyCartCta.ctaUrl"
          />
        </div>

        <product-upsell
          v-if="isMobile"
          :liquid="liquid"
          :products="products"
          :product-count="productCount"
          :recommendations="recommendations"
          product-upsell-splide-settings-cart-drawer
          @has-local-products="handleHasLocalProducts"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import IconClose from '~icons/directional-navigation/close.svg'
import LineItem from '~async/line-item/line-item'
import LineItemCard from '~async/line-item-card/line-item-card'
import FreeShippingIndicator from '~async/free-shipping-indicator/free-shipping-indicator'
import ProductUpsell from '~async/product-upsell/product-upsell'

import { initInsights, sendCheckoutStartedEvent } from '~/helpers/algolia-insights'
import { getAlgoliaConfig } from '~/helpers/algolia-client'

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartSummary,
    DividerLine,
    FreeShippingIndicator,
    IconClose,
    LineItem,
    LineItemCard,
    ProductUpsell,
  },

  props: {
    freeShippingThreshold: {
      type: Number,
      default: 0,
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    emptyCartCta: {
      type: Object,
      default: () => ({}),
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        title: '',
      }),
      required: true,
    },
    productCount: {
      type: Number,
      default: 4,
      required: true,
    },
    products: {
      type: Object,
      default: () => ({
        count: 0,
        dynamic: [],
        section: [],
      }),
      required: true,
    },
    recommendations: {
      type: Object,
      default: () => ({
        enable: true,
        type: 'RELATED',
      }),
      required: true,
    },
  },

  data() {
    return {
      hasLocalProducts: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      groupedCart: 'cart/getGroupedCart',
      freeMembershipThresholdReached: 'collectorsClub/freeMembershipThresholdReached',
      freeMembershipContent: 'collectorsClub/freeMembershipContent',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
      isCollectorsClubMember: (state) => state.collectorsClub.customer.isCollector,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.groupedCart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.groupedCart.items,
      ]
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.refresh({ currentElement: document.activeElement })
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    this.overlay.refresh()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },

    /**
     * Handle has local products.
     * @param {Boolean} value - Whether local products exist.
     */
    handleHasLocalProducts(value = true) {
      this.hasLocalProducts = value
    },

    /**
     * Handle checkout form submit.
     * - Sends Algolia checkout started event before form submits.
     */
    handleCheckoutSubmit() {
      const algoliaIndex = getAlgoliaConfig().mainIndex
      if (!algoliaIndex || !this.cart.items?.length) {
        return
      }

      initInsights()
      sendCheckoutStartedEvent({
        indexName: algoliaIndex,
        objectIDs: this.cart.items.map((item) => item.variant_id),
      })
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
